/**
 * SSIDisplayPreferencesHeader — Fixed floating header bar for toggling visual
 * display preferences during a CYP ASD SSI session.
 *
 * Positioned at the top of the viewport (below the main nav) so the bottom
 * of the screen is free for the Functional Impairment quick-access panel.
 *
 * Collapsible via a small toggle tab so it doesn't distract once set.
 */

import React, { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon, AdjustmentsIcon } from '@heroicons/react/solid';

export default function SSIDisplayPreferencesFooter({ preferences, onPreferenceChange }) {
  const [isExpanded, setIsExpanded] = useState(false);

  const evidenceMode = preferences.evidenceMode || 'dropdown';
  const sidebarMode = preferences.sidebarMode || 'compact';
  const navMode = preferences.navMode || 'bottom';

  return (
    <div className="fixed top-0 left-0 right-0 z-50 flex justify-center pointer-events-none">
      <div className="pointer-events-auto w-full max-w-4xl">
        {/* Preferences panel — drops down from top */}
        <div
          className={`
            overflow-hidden transition-all duration-300 ease-in-out
            ${isExpanded ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'}
          `}
        >
          <div className="bg-white border-b border-gray-200 shadow-[0_4px_20px_rgba(0,0,0,0.08)] px-6 py-3">
            <div className="flex items-center gap-8">

              {/* Evidence display mode toggle */}
              <div className="flex items-center gap-3">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Evidence
                </span>
                <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                  <button
                    onClick={() => onPreferenceChange('evidenceMode', 'dropdown')}
                    className={`
                      px-3 py-1.5 text-xs font-semibold transition-all duration-150
                      ${evidenceMode === 'dropdown'
                        ? 'bg-primary text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-50'}
                    `}
                  >
                    Dropdown
                  </button>
                  <button
                    onClick={() => onPreferenceChange('evidenceMode', 'buttons')}
                    className={`
                      px-3 py-1.5 text-xs font-semibold border-l border-gray-300 transition-all duration-150
                      ${evidenceMode === 'buttons'
                        ? 'bg-primary text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-50'}
                    `}
                  >
                    Buttons
                  </button>
                </div>
              </div>

              {/* Divider */}
              <div className="w-px h-6 bg-gray-200" />

              {/* Sidebar mode toggle */}
              <div className="flex items-center gap-3">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Sidebar
                </span>
                <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                  <button
                    onClick={() => onPreferenceChange('sidebarMode', 'compact')}
                    className={`
                      px-3 py-1.5 text-xs font-semibold transition-all duration-150
                      ${sidebarMode === 'compact'
                        ? 'bg-primary text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-50'}
                    `}
                  >
                    Compact
                  </button>
                  <button
                    onClick={() => onPreferenceChange('sidebarMode', 'expanded')}
                    className={`
                      px-3 py-1.5 text-xs font-semibold border-l border-gray-300 transition-all duration-150
                      ${sidebarMode === 'expanded'
                        ? 'bg-primary text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-50'}
                    `}
                  >
                    Expanded
                  </button>
                </div>
              </div>

              {/* Divider */}
              <div className="w-px h-6 bg-gray-200" />

              {/* Nav buttons mode toggle */}
              <div className="flex items-center gap-3">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Navigation
                </span>
                <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                  <button
                    onClick={() => onPreferenceChange('navMode', 'bottom')}
                    className={`
                      px-3 py-1.5 text-xs font-semibold transition-all duration-150
                      ${navMode === 'bottom'
                        ? 'bg-primary text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-50'}
                    `}
                  >
                    Bottom only
                  </button>
                  <button
                    onClick={() => onPreferenceChange('navMode', 'both')}
                    className={`
                      px-3 py-1.5 text-xs font-semibold border-l border-gray-300 transition-all duration-150
                      ${navMode === 'both'
                        ? 'bg-primary text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-50'}
                    `}
                  >
                    Top &amp; Bottom
                  </button>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* Expand/collapse toggle tab — hangs below the panel */}
        <div className="flex justify-center">
          <button
            onClick={() => setIsExpanded(prev => !prev)}
            className={`
              flex items-center gap-1.5 px-4 py-1 text-xs font-semibold
              rounded-b-lg border border-t-0 border-gray-300
              bg-white text-gray-600 hover:text-primary hover:bg-gray-50
              shadow-lg transition-all duration-200
            `}
            aria-label={isExpanded ? 'Collapse display preferences' : 'Expand display preferences'}
          >
            <AdjustmentsIcon className="w-3.5 h-3.5" />
            <span>Display Preferences</span>
            {isExpanded ? (
              <ChevronUpIcon className="w-3.5 h-3.5" />
            ) : (
              <ChevronDownIcon className="w-3.5 h-3.5" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
